import React from "react";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { MONTHS } from "../types/dashboard.types";
import type { MonthlyIncome } from "../types/dashboard.types";
import { AXIS_TICK, BRAND, BRAND_DARK, MUTED_MARK } from "../chartTheme";

const formatRupees = (value: number) => `₹${value.toLocaleString("en-IN")}`;

/** How much of each month's band Recharts leaves as gap beside a bar. It takes
 *  this off *both* sides of the band, so the bar itself is what remains of the
 *  band after two of them — which is the width the empty state's stubs are
 *  drawn at, so a card with nothing in it has bars the width of the ones that
 *  land in it. */
const BAR_GAP_PERCENT = 28;
const BAR_CATEGORY_GAP = `${BAR_GAP_PERCENT}%`;
const BAR_WIDTH = `${100 - 2 * BAR_GAP_PERCENT}%`;

/** Where the outermost stubs' edges land: one band's gap in from either side of
 *  the row. `NewClientsCard` insets its line by the same amount, so the two
 *  empty cards standing side by side draw their marks to one width rather than
 *  each to the margins of its own chart. */
export const EMPTY_MARK_INSET = `calc(100% / ${MONTHS.length} * ${BAR_GAP_PERCENT} / 100)`;

/** The axis an empty chart draws in place of a plot: a flat mark per month
 *  under its own label, so the card keeps the shape of the chart that will
 *  land here without any of it reading as a month that did better or worse
 *  than another. `marks` is what sits above the labels. */
export const EmptyMonthAxis: React.FC<{ marks: React.ReactNode }> = ({
  marks,
}) => (
  // Hidden from screen readers: twelve month names with nothing measured
  // against them say less than the line above the card already does.
  <div className="mt-auto pt-5" aria-hidden="true">
    {marks}
    <div className="mt-2.5 flex">
      {MONTHS.map((month) => (
        <span
          key={month}
          className="flex-1 text-center text-[11px] 3xl:text-[12px]"
          style={{ color: AXIS_TICK }}
        >
          {month}
        </span>
      ))}
    </div>
  </div>
);

/**
 * The floor under the two cards that plot a year — this one and `NewClientsCard`
 * — so the block that stands in for one is the size of the card that lands.
 *
 * A plotted year is a fixed-height plot under two lines of heading and comes to
 * about this anyway; what the floor is really holding up is the other state,
 * where there is no series and the card is a caption over twelve flat marks.
 * Left to its own height that collapses to a strip a third of the card beside
 * it, and a workspace with nothing to plot yet is exactly the one that sees it —
 * on the first run both of these cards are in that state at once.
 *
 * `TotalClientsCard` shares the same floor: the three stand in one grid row and
 * the row is as tall as its tallest. All three are only ever drawn from `md` up,
 * which is the one width this grid is built at — a phone gets three cards of its
 * own instead, and the two that plot a year are redrawn there for a thumb rather
 * than narrowed. See `MobileDashboard`.
 */
export const CHART_CARD_FOOTPRINT = "min-h-[250px] 3xl:min-h-[328px]";

interface EarningsCardProps {
  /**
   * Rupees taken in all year, as the dashboard endpoint counted them. `null`
   * while there is no figure, which reads as ₹0 — the total of nothing measured
   * is nothing, and this is the one card whose headline is money rather than a
   * count, so a dash here would read as broken rather than as unmeasured.
   */
  total: number | null;
  /**
   * A rupee figure per month of the current year, as the endpoint sends it.
   * Empty until it arrives, which plots nothing rather than a year the data has
   * not confirmed.
   */
  data: MonthlyIncome[];
  /**
   * Whether this workspace has taken any money yet. A dietitian with no clients
   * has not, so the card says ₹0 and shows the shape a year of earnings will
   * take rather than a plot of twelve zeros — an axis of empty months reads as
   * a bad year rather than as a workspace that has not started.
   *
   * It is not the only thing that puts the card into that state: an empty
   * `data` does too, whoever the workspace belongs to. See `showsAxis`.
   */
  empty?: boolean;
  /**
   * Where the dashboard call is. The card is not drawn until it answers — a
   * total that changed a moment after landing reads as a figure being taken
   * back — and a wait long enough to notice shimmers the whole card.
   */
  phase?: LoadPhase;
}

const EarningsCard: React.FC<EarningsCardProps> = ({
  total,
  data,
  empty = false,
  phase = "ready",
}) => {
  const headline = empty ? 0 : total ?? 0;

  /**
   * Whether the card shows marks instead of a plot.
   *
   * A bar chart needs a year to draw. The endpoint sends `income_by_month:
   * null` for a year it has nothing to total — which the service maps to no
   * rows rather than to twelve zeros, since the two are not the same figure —
   * and Recharts draws exactly what it is given: a card with a hole where the
   * chart goes. That happens well after the first run, too, on a workspace with
   * clients on the books whose earnings the backend has not counted, so the
   * axis stands in whenever there is no series, not only on a workspace that
   * has not started.
   */
  const showsAxis = empty || data.length === 0;

  return phase !== "ready" ? (
    <SkeletonCard phase={phase} className={CHART_CARD_FOOTPRINT} />
  ) : (
    <section
      className={`rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-7 flex flex-col ${CHART_CARD_FOOTPRINT}`}
    >
      <h2 className="text-[18px] 3xl:text-[21px] font-semibold text-ink">
        {formatRupees(headline)}
      </h2>
      {/* The caption labels the figure above it, not the plot below — so a
          total the backend did send is still "Total earnings" on a card with
          nothing to chart. It is only where there is neither a year to draw nor
          a rupee to name that it says so in words. */}
      <p className="text-[13px] 3xl:text-[15px] text-ink-muted">
        {showsAxis && headline === 0 ? "No earnings yet" : "Total earnings"}
      </p>

      {showsAxis ? (
        // Marks, not data: a flat stub per month, all the same length and all
        // on one line, so nothing here can be read as a month that did better
        // than another. The ₹0 above is what says there is nothing to plot.
        <EmptyMonthAxis
          marks={
            // Each stub sits in its own twelfth of the row and takes the same
            // share of it a bar does, so the marks start and end where the
            // plotted bars would rather than running to the card's edges.
            <div className="flex items-end">
              {MONTHS.map((month) => (
                <span key={month} className="flex-1 flex justify-center">
                  <span
                    className="h-[3px] rounded-full"
                    style={{ backgroundColor: BRAND, width: BAR_WIDTH }}
                  />
                </span>
              ))}
            </div>
          }
        />
      ) : (
        /* mt-auto pins the plot to the card's bottom edge. Cards in a grid row
           are equal height, so this keeps the month labels on one line across
           cards even though the headers above them differ in height. */
        <div className="mt-auto pt-5 h-[150px] 3xl:h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
              barCategoryGap={BAR_CATEGORY_GAP}
            >
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#9AA4AE", fontSize: 11 }}
                dy={4}
              />
              <Tooltip
                // No cursor: Recharts otherwise paints a tinted column the full
                // height of the plot behind the hovered bar, which reads as a
                // second, ghostly bar beside the real one. The tooltip alone says
                // which month is being pointed at.
                cursor={false}
                content={({ active, payload, label }) => {
                  const point = payload?.[0]?.payload as
                    | MonthlyIncome
                    | undefined;
                  if (!point) return null;
                  return (
                    <ChartTooltip
                      active={active}
                      label={String(label)}
                      swatch={point.status === "future" ? MUTED_MARK : BRAND}
                      value={
                        point.status === "future"
                          ? "No data yet"
                          : formatRupees(point.amount)
                      }
                      name={point.status === "future" ? undefined : "earned"}
                    />
                  );
                }}
              />
              {/* Recharts clamps each corner radius to half the bar's shorter
                side, so an oversized value yields a full pill on tall bars and
                a rounded stub on the short "no data" marks.
                minPointSize renders months with no data as that flat stub
                rather than inventing a bar height for them. */}
              <Bar
                dataKey="amount"
                radius={[999, 999, 999, 999]}
                minPointSize={8}
              >
                {data.map((entry) => (
                  <Cell
                    key={entry.month}
                    fill={
                      entry.status === "future"
                        ? MUTED_MARK
                        : entry.status === "current"
                          ? "#FFFFFF"
                          : BRAND
                    }
                    stroke={entry.status === "current" ? BRAND_DARK : undefined}
                    strokeWidth={entry.status === "current" ? 1.5 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};

export default EarningsCard;
